import { Dispatch, SetStateAction, useState } from "react";
import { Box, Button, Chip, Grid2, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { NoPlaceZoneBooking } from "../../../../features/Booking-Form/typings/booking-service-administration.types";
import { CustomColoredTabs } from "../../../CustomColoredTabs/CustomColoredTabs.component";
import { BookingServiceCustomTab } from "../../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/partial/BookingServiceCustomTab.partial";
import { TabPanel } from "../../../../features/TabPanel/TabPanel.component";
import ScheduleSelectionServicesTabCatering from "./partial/ScheduleSelection-Tab-Services-Catering.partial";
import { ServiceEquipmentDataTable } from "./partial/ScheduleSelection-Tab-Services-EquipmentDataTable.partial";
import {
  ServiceRoomLayoutData,
  ServiceRoomLayoutDataTable
} from "./partial/ScheduleSelection-Tab-Services-RoomLayoutDataTable.partial";
import { tabHeader } from "../../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/functions/bookingServiceTabHeader.function";
import { ServiceData } from "./ScheduleSelection-Tab-Services.component";
import { MaxSetUpTime } from "../../../../features/Booking-Form/typings/booking-services.types";

type P = {
  schedule: NoPlaceZoneBooking;
  isMobileTablet: boolean;
  isEditable: boolean;
  cateringData: ServiceData[];
  equipmentData: ServiceData[];
  layoutData: ServiceData[];
  visibleTabs: { label: string; index: number; count: number; orderable: number; testId: string }[];
  remoteMaxSetupTime: MaxSetUpTime | undefined;
  selectCostCenter: { id: string; name: string } | null;
  setCateringData: Dispatch<SetStateAction<ServiceData[]>>;
  setSelectCostCenter: Dispatch<SetStateAction<{ id: string; name: string } | null>>;
  setEquipmentData: Dispatch<SetStateAction<ServiceData[]>>;
  setLayoutData: Dispatch<SetStateAction<ServiceData[]>>;
  handleConfirmservice: () => Promise<void>;
};

export const ScheduleSelectionServicesContents: React.FC<P> = ({
  schedule,
  isMobileTablet,
  isEditable,
  cateringData,
  equipmentData,
  layoutData,
  visibleTabs,
  remoteMaxSetupTime,
  selectCostCenter,
  setCateringData,
  setSelectCostCenter,
  setEquipmentData,
  setLayoutData,
  handleConfirmservice
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const tableSxStyle = {
    fontSize: isMobileTablet ? 12 : 17,
    fontWeight: theme.typography.fontWeightRegular,
    pl: 1,
    pr: 1,
    "&.Mui-selected": {
      color:
        theme.palette.mode === "dark"
          ? theme.palette.getContrastText(theme.palette.primary.main)
          : theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold
    }
  };

  const initialTab =
    visibleTabs.find(tab => tab.count > 0)?.index ??
    visibleTabs.find(tab => tab.orderable > 0)?.index ??
    0;
  const [selectTab, setSelectTab] = useState(initialTab);
  const handleChange = (e: any, newValue: number) => setSelectTab(newValue);
  const customTabChipColor = (index: number) => (selectTab === index ? "primary" : "secondary");

  return (
    <Stack gap={1} width={"100%"}>
      {/* tabs header */}
      <Grid2 container data-testid="booking-selection-dialog-contents-service-tabs" mt={1.5}>
        <CustomColoredTabs value={selectTab} onChange={handleChange} autoScrollable hideIndicator>
          {visibleTabs.map(({ label, index, count, testId }) => (
            <BookingServiceCustomTab
              key={testId}
              index={index}
              value={index}
              icon={<Chip color={customTabChipColor(index)} label={tabHeader(label, count)} />}
              data-testid={testId}
              sx={tableSxStyle}
            />
          ))}
        </CustomColoredTabs>
      </Grid2>

      {/* tab contents */}
      {/* catering tab */}
      <TabPanel value={selectTab} index={0} widthVal={"inherit"}>
        <ScheduleSelectionServicesTabCatering
          isMobile={isMobile}
          cateringData={cateringData}
          setCateringData={setCateringData}
          isEditable={isEditable}
          selectCostCenter={selectCostCenter}
          setSelectCostCenter={setSelectCostCenter}
          schedule={schedule}
          remoteMaxSetupTime={remoteMaxSetupTime}
        />
      </TabPanel>

      {/* additional equipment tab */}
      <TabPanel value={selectTab} index={1} widthVal={"inherit"}>
        <ServiceEquipmentDataTable
          equipmentData={equipmentData}
          setEquipmentData={setEquipmentData}
          isEditable={isEditable}
        />
      </TabPanel>

      {/* room layout tab */}
      <TabPanel value={selectTab} index={2} widthVal={"inherit"}>
        <ServiceRoomLayoutDataTable
          layoutData={layoutData as ServiceRoomLayoutData[]}
          setLayoutData={setLayoutData}
          isEditable={isEditable}
        />
      </TabPanel>

      {isEditable && (
        <Box sx={{ display: "flex", justifyContent: "right", mr: 1 }}>
          <Button data-testid="confirm-service-btn" onClick={handleConfirmservice}>
            {t("Confirm")}
          </Button>
        </Box>
      )}
    </Stack>
  );
};
